import { IMG } from "./images";

// Planning pages for /plan/:slug — content is block-based and rendered by SectionRenderer.
export const PLANNING = [
  {
    slug: "opening-hours",
    title: "Colosseum Opening Hours",
    subtitle: "Seasonal hours, last entry times and the days the Colosseum is closed.",
    heroImage: IMG.exteriorDay,
    intro: "The Colosseum opens early and adjusts its closing time with the seasons, tracking the setting sun. Last admission is typically one hour before closing, so plan your timed-entry slot accordingly.",
    blocks: [
      {
        type: "table",
        heading: "Seasonal opening hours",
        columns: ["Period", "Opens", "Last entry / Closes"],
        rows: [
          ["1 Jan – 28 Feb", "9:00", "16:30"],
          ["1 Mar – late Mar", "9:00", "17:30"],
          ["Late Mar – 31 Aug", "9:00", "19:15"],
          ["1 Sep – 30 Sep", "9:00", "19:00"],
          ["1 Oct – late Oct", "9:00", "18:30"],
          ["Late Oct – 31 Dec", "9:00", "16:30"],
        ],
      },
      {
        type: "alert",
        tone: "info",
        title: "Last admission",
        text: "Final entry is usually one hour before the listed closing time. The ticket office and standby queue close earlier still — another reason to book a timed slot in advance.",
      },
      {
        type: "tips",
        heading: "Good to know",
        items: [
          { icon: "Sunrise", title: "First slot", text: "The 9:00 opening is the calmest, coolest and most photogenic time to arrive." },
          { icon: "CalendarX", title: "Closed days", text: "The Colosseum is closed on 25 December and 1 January." },
          { icon: "Clock", title: "Allow time", text: "Budget 1.5–2 hours for the Colosseum and another 1.5–2 hours for the Forum and Palatine Hill." },
        ],
      },
    ],
    relatedTickets: ["entry", "skip-the-line", "last-minute"],
    relatedPlanning: [
      { label: "Best Time to Visit", to: "/plan/best-time-to-visit" },
      { label: "How to Reach", to: "/plan/how-to-reach" },
      { label: "Planning FAQs", to: "/plan/faqs" },
    ],
  },
  {
    slug: "best-time-to-visit",
    title: "Best Time to Visit the Colosseum",
    subtitle: "The quietest hours, the best months and how to dodge the crowds and heat.",
    heroImage: IMG.heroGolden,
    intro: "The Colosseum welcomes over seven million visitors a year, but crowds ebb and flow predictably. With the right timing you can trade shoulder-to-shoulder queues for space, cooler air and golden light.",
    blocks: [
      {
        type: "timeline",
        heading: "The day, hour by hour",
        items: [
          { label: "9:00–10:00", title: "Best of the day", text: "Doors open, temperatures are mild and the first-slot crowd is thin. Ideal for photos and a relaxed pace." },
          { label: "11:00–15:00", title: "Peak crowds", text: "Tour groups and day-trippers converge. Expect the busiest stands and longest security waits." },
          { label: "15:00–17:00", title: "Easing off", text: "Numbers gradually thin as groups depart. A solid second-best window outside high summer." },
          { label: "After 17:00", title: "Golden hour", text: "In summer, late slots reward you with warm light and shrinking crowds. Night tours go further still." },
        ],
      },
      {
        type: "twocol",
        heading: "Best and busiest months",
        left: { title: "Quietest & most pleasant", items: ["November to early March (cool, calm)", "Weekdays over weekends", "Early November & late January"] },
        right: { title: "Busiest & hottest", items: ["June to August (heat + peak crowds)", "Easter week & public holidays", "Weekends and midday year-round"] },
      },
      {
        type: "alert",
        tone: "warning",
        title: "Avoid the midday summer slot",
        text: "Between June and August, the 11:00–15:00 window combines fierce sun with the heaviest crowds. If you must visit then, prioritise a skip-the-line ticket and bring water and a hat.",
      },
    ],
    relatedTickets: ["skip-the-line", "night-tours", "guided-tours"],
    relatedPlanning: [
      { label: "Opening Hours", to: "/plan/opening-hours" },
      { label: "Tips for Visiting", to: "/plan/tips" },
      { label: "Rules & Regulations", to: "/plan/rules" },
    ],
  },
  {
    slug: "how-to-reach",
    title: "How to Reach the Colosseum",
    subtitle: "Metro, bus, tram, taxi and walking routes to Piazza del Colosseo.",
    heroImage: IMG.horseCarriage,
    intro: "The Colosseum sits at the heart of Rome and is one of the easiest landmarks in the city to reach. The metro drops you almost at its feet, and several bus and tram lines stop within a short walk.",
    blocks: [
      {
        type: "tips",
        heading: "Getting there",
        items: [
          { icon: "TrainFront", title: "By Metro", text: "Line B to 'Colosseo' station — the exit faces the monument directly. The simplest option from most of the city." },
          { icon: "Bus", title: "By Bus", text: "Lines 51, 75, 85, 87 and 118 stop along Via dei Fori Imperiali and Piazza del Colosseo." },
          { icon: "TramFront", title: "By Tram", text: "Tram line 3 stops nearby, linking the Colosseum with Trastevere and the Borghese area." },
          { icon: "Car", title: "By Taxi", text: "Ask for 'Piazza del Colosseo'. Official white taxis use meters; agree on airport flat fares in advance." },
        ],
      },
      {
        type: "steps",
        heading: "From the main arrival points",
        items: [
          { title: "From Termini Station", text: "Take Metro Line B (direction Laurentina) two stops to Colosseo. Journey time around 5 minutes." },
          { title: "From the Vatican", text: "Metro Line A to Termini, then change to Line B for Colosseo. Allow 30–40 minutes door to door." },
          { title: "From Trastevere", text: "Hop on tram line 3 or walk across the river via the historic centre in roughly 25–30 minutes." },
        ],
      },
      {
        type: "alert",
        tone: "info",
        title: "Walking is a pleasure here",
        text: "If you're staying in the historic centre, walking to the Colosseum past the Roman Forum and Via dei Fori Imperiali is one of Rome's great approaches — the monument reveals itself dramatically as you round the avenue.",
      },
    ],
    relatedTickets: ["entry", "skip-the-line", "combo"],
    relatedPlanning: [
      { label: "Entrances", to: "/plan/entrances" },
      { label: "Parking", to: "/plan/parking" },
      { label: "Interactive Map", to: "/plan/map" },
    ],
  },
  {
    slug: "entrances",
    title: "Colosseum Entrances",
    subtitle: "Which gate to use for your ticket type — and how to find your meeting point.",
    heroImage: IMG.exteriorArches,
    intro: "The Colosseum has separate entrances for different ticket types. Using the correct gate saves time and confusion, especially for guided tours and restricted-area access.",
    blocks: [
      {
        type: "table",
        heading: "Entrance by ticket type",
        columns: ["Ticket type", "Where to go"],
        rows: [
          ["Standard & skip-the-line entry", "Main visitor entrance on Piazza del Colosseo"],
          ["Guided tours", "Operator meeting point — usually near the Arch of Constantine"],
          ["Arena floor / underground", "Dedicated gate, accessed with your guide after a name/ID check"],
          ["Roman Forum & Palatine Hill", "Via dei Fori Imperiali or Via di San Gregorio entrances"],
        ],
      },
      {
        type: "checklist",
        heading: "Before you reach the gate",
        tone: "do",
        items: [
          "Have your QR code open and screen brightness up",
          "Carry photo ID — essential for reduced tickets and restricted areas",
          "Travel light; large bags must be checked and slow you down",
          "Arrive 15–20 minutes early for security screening",
        ],
      },
      {
        type: "alert",
        tone: "warning",
        title: "Meeting points vary",
        text: "Guided tours rarely meet at the main gate. Read your booking confirmation carefully — the meeting point is often near the Arch of Constantine or a nearby café, not the Colosseum entrance itself.",
      },
    ],
    relatedTickets: ["guided-tours", "underground", "arena-floor"],
    relatedPlanning: [
      { label: "How to Reach", to: "/plan/how-to-reach" },
      { label: "Rules & Regulations", to: "/plan/rules" },
      { label: "Tips for Visiting", to: "/plan/tips" },
    ],
  },
  {
    slug: "map",
    title: "Colosseum Area Map",
    subtitle: "Orient yourself: the Colosseum, Forum, Palatine Hill and the metro at a glance.",
    heroImage: IMG.forumAerial,
    intro: "The archaeological park forms a compact triangle in central Rome. Once you understand how the Colosseum, Roman Forum and Palatine Hill connect, planning your route becomes simple.",
    blocks: [
      {
        type: "map",
        heading: "Find the Colosseum",
        embed: "https://www.google.com/maps?q=Colosseum,Rome&output=embed",
        caption: "Piazza del Colosseo, 1, 00184 Roma RM, Italy",
      },
      {
        type: "tips",
        heading: "Key points of orientation",
        items: [
          { icon: "Landmark", title: "The Colosseum", text: "The eastern anchor of the park, beside the Arch of Constantine and Colosseo metro station." },
          { icon: "Columns", title: "Roman Forum", text: "Stretches north-west along Via dei Fori Imperiali, between the Colosseum and Piazza Venezia." },
          { icon: "Mountain", title: "Palatine Hill", text: "Rises to the south of the Forum, offering the best elevated views back over the ruins." },
        ],
      },
      {
        type: "alert",
        tone: "info",
        title: "One ticket, three sites",
        text: "Most Colosseum tickets include the Forum and Palatine Hill. They're walkable from one another, so build a single loop rather than crossing the city between them.",
      },
    ],
    relatedTickets: ["entry", "combo", "guided-tours"],
    relatedPlanning: [
      { label: "How to Reach", to: "/plan/how-to-reach" },
      { label: "Nearby Attractions", to: "/plan/nearby-attractions" },
      { label: "Entrances", to: "/plan/entrances" },
    ],
  },
  {
    slug: "rules",
    title: "Rules & Regulations",
    subtitle: "Security checks, prohibited items and the etiquette that keeps the visit smooth.",
    heroImage: IMG.interiorCrowd,
    intro: "The Colosseum is a protected UNESCO World Heritage monument with airport-style security. Knowing the rules before you arrive means a faster entry and no surprises at the gate.",
    blocks: [
      {
        type: "twocol",
        heading: "What you can and can't bring",
        left: { title: "Allowed", items: ["Small bags and handbags", "Water in a reusable bottle", "Cameras and phones", "Folded umbrellas"] },
        right: { title: "Not allowed", items: ["Large bags, suitcases & backpacks", "Glass bottles & alcohol", "Tripods, selfie sticks & drones", "Sharp objects & sprays"] },
      },
      {
        type: "checklist",
        heading: "Visitor etiquette",
        tone: "do",
        items: [
          "Keep to marked paths and behind barriers",
          "Do not touch, climb on or remove any stone",
          "Keep voices low inside the underground passages",
          "Dispose of litter in the bins provided",
        ],
      },
      {
        type: "alert",
        tone: "warning",
        title: "No cloakroom for large luggage",
        text: "There is no left-luggage facility for suitcases. If you're visiting between hotels or before a flight, store bags at your accommodation or a city luggage service first — you will be turned away with oversized bags.",
      },
    ],
    relatedTickets: ["entry", "skip-the-line", "family"],
    relatedPlanning: [
      { label: "Entrances", to: "/plan/entrances" },
      { label: "Tips for Visiting", to: "/plan/tips" },
      { label: "Planning FAQs", to: "/plan/faqs" },
    ],
  },
  {
    slug: "tips",
    title: "Tips for Visiting the Colosseum",
    subtitle: "Hard-won advice to make your visit smoother, cooler and more memorable.",
    heroImage: IMG.womanColosseum,
    intro: "A little preparation transforms a good Colosseum visit into a great one. These are the practical tips our team and travellers return to again and again.",
    blocks: [
      {
        type: "tips",
        heading: "Our top tips",
        items: [
          { icon: "Sunrise", title: "Go early or late", text: "The first and last slots of the day are cooler, quieter and far better for photography." },
          { icon: "Footprints", title: "Wear good shoes", text: "Ancient cobbles and uneven terrain across three sites demand comfortable, closed footwear." },
          { icon: "Droplet", title: "Bring water", text: "Refill free at Rome's nasoni drinking fountains — there's one near the entrance." },
          { icon: "Backpack", title: "Pack light", text: "Skip the big bag; it must be checked at security and slows your entry." },
          { icon: "Sun", title: "Sun protection", text: "There's little shade inside. A hat, sunscreen and sunglasses are essential in summer." },
          { icon: "Headphones", title: "Add context", text: "A guide or audio guide turns bare stone into vivid, unforgettable history." },
        ],
      },
      {
        type: "alert",
        tone: "info",
        title: "Reserve the underground early",
        text: "If the Hypogeum or arena floor is on your list, book several weeks ahead. These restricted-area tickets are capped daily and sell out faster than any other option.",
      },
    ],
    relatedTickets: ["skip-the-line", "underground", "guided-tours"],
    relatedPlanning: [
      { label: "Best Time to Visit", to: "/plan/best-time-to-visit" },
      { label: "Rules & Regulations", to: "/plan/rules" },
      { label: "Restaurants Nearby", to: "/plan/restaurants" },
    ],
  },
  {
    slug: "parking",
    title: "Parking Near the Colosseum",
    subtitle: "Where to park, why public transport usually wins, and the ZTL zones to avoid.",
    heroImage: IMG.exteriorClose,
    intro: "Central Rome is not built for cars, and the Colosseum has no dedicated visitor car park. Most travellers are better off arriving by metro — but if you must drive, here's how to do it sensibly.",
    blocks: [
      {
        type: "table",
        heading: "Parking options nearby",
        columns: ["Option", "Approx. distance", "Notes"],
        rows: [
          ["Garage Roma Termini area", "~1.5 km", "Secure paid garages near the main station"],
          ["Street parking (blue lines)", "Varies", "Paid via meter; very limited and often full"],
          ["Park & Ride (metro)", "Outer stations", "Park at outer Line B stops and ride in — recommended"],
          ["Tour coach drop-off", "Designated bays", "For organised groups only"],
        ],
      },
      {
        type: "alert",
        tone: "warning",
        title: "Beware the ZTL",
        text: "The historic centre is a Limited Traffic Zone (ZTL) monitored by cameras. Driving in without a permit results in automatic fines. The streets immediately around the Colosseum are largely pedestrianised.",
      },
      {
        type: "checklist",
        heading: "If you're driving in",
        tone: "do",
        items: [
          "Use a Park & Ride at an outer metro station and take Line B in",
          "Never enter a ZTL zone without authorisation",
          "Book a garage space online in advance during peak season",
          "Factor parking cost into a combo vs. taxi comparison",
        ],
      },
    ],
    relatedTickets: ["entry", "combo", "family"],
    relatedPlanning: [
      { label: "How to Reach", to: "/plan/how-to-reach" },
      { label: "Interactive Map", to: "/plan/map" },
      { label: "Entrances", to: "/plan/entrances" },
    ],
  },
  {
    slug: "restaurants",
    title: "Restaurants Near the Colosseum",
    subtitle: "Where to eat well near the monument — and the tourist traps to sidestep.",
    heroImage: IMG.pasta,
    intro: "The streets right beside the Colosseum lean touristy, but walk a few minutes into the Monti district and you'll find some of Rome's most characterful trattorie. Here's how to eat well close to the action.",
    blocks: [
      {
        type: "tips",
        heading: "Where to head",
        items: [
          { icon: "Utensils", title: "Monti district", text: "A 5–10 minute walk north — Rome's oldest neighbourhood, full of authentic, well-priced trattorie." },
          { icon: "Coffee", title: "Quick espresso & pizza", text: "Grab a stand-up espresso or a slice of pizza al taglio for an efficient, local-style refuel." },
          { icon: "Wine", title: "Aperitivo", text: "Wind down with a spritz and snacks in a Monti wine bar after your visit." },
        ],
      },
      {
        type: "checklist",
        heading: "Avoid the tourist traps",
        tone: "dont",
        items: [
          "Restaurants with photos of every dish on the menu",
          "Staff actively waving you in from the street",
          "'Tourist menus' at suspiciously round prices right by the gate",
          "Anywhere with no Italians eating inside",
        ],
      },
      {
        type: "alert",
        tone: "info",
        title: "Eat before or after, not during",
        text: "No food is sold inside the Colosseum and re-entry isn't allowed. Plan your meal around your visit — a light bite beforehand and a proper lunch in Monti afterwards works beautifully.",
      },
    ],
    relatedTickets: ["family", "guided-tours", "combo"],
    relatedPlanning: [
      { label: "Nearby Attractions", to: "/plan/nearby-attractions" },
      { label: "Tips for Visiting", to: "/plan/tips" },
      { label: "How to Reach", to: "/plan/how-to-reach" },
    ],
  },
  {
    slug: "nearby-attractions",
    title: "Nearby Attractions",
    subtitle: "Build a perfect day around the Colosseum with these walkable Roman highlights.",
    heroImage: IMG.forumColumns,
    intro: "The Colosseum is the gateway to ancient Rome, and many of the city's icons lie within easy walking distance. String them together for an unforgettable day on foot.",
    blocks: [
      {
        type: "tips",
        heading: "Within walking distance",
        items: [
          { icon: "Columns", title: "Roman Forum", text: "Included with your ticket — the beating heart of the ancient city, right next door." },
          { icon: "Mountain", title: "Palatine Hill", text: "Also included; climb for sweeping views over the Forum and Circus Maximus." },
          { icon: "Landmark", title: "Capitoline Hill", text: "Michelangelo's piazza and world-class museums, a 10-minute walk away." },
          { icon: "Building", title: "Pantheon", text: "Rome's best-preserved ancient temple, about 20 minutes on foot through the centre." },
          { icon: "Coins", title: "Trevi Fountain", text: "Toss a coin to ensure your return to Rome, roughly 25 minutes away." },
          { icon: "Church", title: "Vittoriano", text: "The vast white monument at Piazza Venezia with a panoramic terrace." },
        ],
      },
      {
        type: "alert",
        tone: "info",
        title: "Two icons in a day?",
        text: "Pairing the Colosseum with the Vatican is ambitious but doable. Read our dedicated one-day guide for a realistic, low-stress plan.",
      },
    ],
    relatedTickets: ["combo", "guided-tours", "entry"],
    relatedPlanning: [
      { label: "Interactive Map", to: "/plan/map" },
      { label: "Restaurants Nearby", to: "/plan/restaurants" },
      { label: "How to Reach", to: "/plan/how-to-reach" },
    ],
  },
  {
    slug: "faqs",
    title: "Planning FAQs",
    subtitle: "Quick answers to the questions visitors ask most before they arrive.",
    heroImage: IMG.exteriorTrees,
    intro: "Everything from tickets and timing to accessibility and security, answered in one place. Still unsure? Each ticket and planning page carries its own detailed FAQ section too.",
    blocks: [
      {
        type: "accordion",
        heading: "Frequently asked questions",
        items: [
          { q: "Do I need to book tickets in advance?", a: "Strongly recommended. Entry is timed and capacity is capped daily; on-site tickets regularly sell out and the standby queue can exceed 90 minutes." },
          { q: "How long should I budget for the visit?", a: "Around 1.5–2 hours for the Colosseum and a similar amount for the Forum and Palatine Hill — roughly half a day for all three." },
          { q: "Is the Colosseum wheelchair accessible?", a: "The main tiers are largely accessible via lifts and ramps, and entry is free for visitors with disabilities and a companion. The underground and arena floor are not accessible due to steps." },
          { q: "Are the Roman Forum and Palatine Hill included?", a: "Most tickets include a combined, single entry to both, valid the same day or within 24 hours. Always check your specific ticket." },
          { q: "What happens if I'm late for my time slot?", a: "There is usually a short grace window, but arriving significantly late can mean being turned away. Plan to reach security 15–20 minutes early." },
          { q: "Can I re-enter after leaving?", a: "No. Once you exit the Colosseum you cannot re-enter on the same ticket." },
        ],
      },
    ],
    relatedTickets: ["entry", "skip-the-line", "guided-tours"],
    relatedPlanning: [
      { label: "Opening Hours", to: "/plan/opening-hours" },
      { label: "Rules & Regulations", to: "/plan/rules" },
      { label: "Tips for Visiting", to: "/plan/tips" },
    ],
  },
];

export const getPlanning = (slug) => PLANNING.find((p) => p.slug === slug);
